import { useState, type FormEvent } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  createNote,
  deleteNote,
  listNotesByEntity,
  updateNote,
} from "../lib/api-modules/notes.js";
import type {
  MembershipRole,
  Note,
  NoteRelatedEntityType,
} from "../lib/api-modules/types.js";
import { ErrorMessage } from "./auth/error-message.js";
import { ConfirmButton } from "./confirm-button.js";

/**
 * Polymorphic notes panel — reusable across any entity drilldown.
 *
 *   <NotesPanel entityType="account" entityId={accountId} />
 *
 * Renders the household's notes attached to that entity, newest-first.
 * Inline create form (when caller has write role); per-note edit +
 * archive controls visible to the author or owner/admin (per the
 * locked Phase 4.2 cross-author edit policy: author-only on content,
 * mod-archivable for moderation).
 *
 * Read access for every active member; the backend enforces the
 * write-role + author-only gates and returns 403 with a descriptive
 * message that this UI shows inline on mutation error.
 *
 * Body rendering is plain text (`whitespace-pre-wrap`) for v1 —
 * markdown can land in a future cleave without an API change.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);
const MOD_ROLES: ReadonlySet<MembershipRole> = new Set(["owner", "admin"]);

export interface NotesPanelProps {
  entityType: NoteRelatedEntityType;
  entityId: string;
  /** Optional override (for tests / read-only-mode). Defaults to role-derived. */
  canWriteOverride?: boolean;
}

export function NotesPanel({
  entityType,
  entityId,
  canWriteOverride,
}: NotesPanelProps) {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite =
    canWriteOverride ?? !READ_ONLY_ROLES.has(user.role);
  const isModerator = MOD_ROLES.has(user.role);

  const queryKey = ["notes", "entity", entityType, entityId] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => listNotesByEntity({ entity_type: entityType, entity_id: entityId }),
  });

  return (
    <section
      aria-labelledby={`notes-panel-${entityId}`}
      className="mt-4 rounded-md border border-zinc-200 p-4 dark:border-zinc-800"
      data-testid="notes-panel"
    >
      <h3
        id={`notes-panel-${entityId}`}
        className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-400"
      >
        Notes
      </h3>

      {canWrite && (
        <CreateNoteForm
          entityType={entityType}
          entityId={entityId}
          queryKey={queryKey}
        />
      )}

      <div className="mt-3 space-y-2">
        {query.isPending && (
          <p className="text-sm text-zinc-500">Loading…</p>
        )}
        {query.isError && (
          <ErrorMessage>
            Couldn't load notes:{" "}
            {query.error instanceof Error ? query.error.message : "unknown"}
          </ErrorMessage>
        )}
        {query.data && query.data.notes.length === 0 && (
          <p className="text-sm text-zinc-500">No notes yet.</p>
        )}
        {query.data && query.data.notes.length > 0 && (
          <ul className="space-y-2">
            {query.data.notes.map((n) => (
              <li
                key={n.note_sk}
                className="rounded-md border border-zinc-200 px-3 py-2 dark:border-zinc-800"
                data-testid={`note-${n.note_id}`}
              >
                <NoteCard
                  note={n}
                  canEdit={n.author_user_id === user.user_id}
                  canArchive={
                    n.author_user_id === user.user_id || isModerator
                  }
                  queryKey={queryKey}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function NoteCard({
  note,
  canEdit,
  canArchive,
  queryKey,
}: {
  note: Note;
  canEdit: boolean;
  canArchive: boolean;
  queryKey: readonly unknown[];
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <EditNoteForm
        note={note}
        onDone={() => setEditing(false)}
        queryKey={queryKey}
      />
    );
  }
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 text-sm">
        <p className="whitespace-pre-wrap text-zinc-800 dark:text-zinc-200">
          {note.body}
        </p>
        <p className="mt-1 text-xs text-zinc-500">
          {new Date(note.timestamp).toLocaleString()}
          {note.updated_at !== note.created_at && (
            <span className="ml-2 italic text-zinc-400">(edited)</span>
          )}
        </p>
      </div>
      {(canEdit || canArchive) && (
        <div className="flex shrink-0 items-center gap-1">
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-zinc-300 px-1.5 py-0.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              Edit
            </button>
          )}
          {canArchive && (
            <ArchiveNoteButton noteSk={note.note_sk} queryKey={queryKey} />
          )}
        </div>
      )}
    </div>
  );
}

function ArchiveNoteButton({
  noteSk,
  queryKey,
}: {
  noteSk: string;
  queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => deleteNote(noteSk),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  return (
    <ConfirmButton
      label="Archive"
      pendingLabel="…"
      size="xs"
      onConfirm={() => mutation.mutate()}
      isPending={mutation.isPending}
    />
  );
}

function CreateNoteForm({
  entityType,
  entityId,
  queryKey,
}: {
  entityType: NoteRelatedEntityType;
  entityId: string;
  queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createNote({
        related_entity_type: entityType,
        related_entity_id: entityId,
        body: body.trim(),
      }),
    onSuccess: async () => {
      setBody("");
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't create note. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <label
        htmlFor={`new-note-${entityId}`}
        className="sr-only"
      >
        New note
      </label>
      <textarea
        id={`new-note-${entityId}`}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add a note (e.g. why we hold this lot)…"
        rows={2}
        maxLength={10_000}
        className="w-full resize-y rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={mutation.isPending}
      />
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending || body.trim().length === 0}
          className="rounded-md bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : "Add note"}
        </button>
      </div>
      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

function EditNoteForm({
  note,
  onDone,
  queryKey,
}: {
  note: Note;
  onDone: () => void;
  queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState(note.body);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => updateNote(note.note_sk, { body: body.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      onDone();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't save. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <textarea
        aria-label="Edit note"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        maxLength={10_000}
        className="w-full resize-y rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={mutation.isPending}
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={mutation.isPending || body.trim().length === 0}
          className="rounded-md bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onDone}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>
      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}
